import { useRef, useState } from "react";
import { Check, FileText, Loader2, Lock, Upload } from "lucide-react";

import { cn } from "@/lib/utils";
import { fileToBase64 } from "@/utils/fileToBase64";
import { useOnboardingDocuments } from "@/hooks/queries/useOnboardingQualification";
import { useUploadOnboardingDocument } from "@/hooks/mutations/useOnboardingQualificationMutations";
import {
  ActionBar,
  ActionButton,
  Eyebrow,
  ScreenLead,
  ScreenTitle,
} from "../OnboardingShell";
import type { SaleCopy } from "./contractorLine";

// Alineado con MAX_UPLOAD_BYTES del backend: el archivo viaja en base64.
const MAX_FILE_BYTES = 40 * 1024 * 1024;

type UploadRow = {
  name: string;
  status: "uploading" | "uploaded" | "error";
  message?: string;
};

const isPdf = (file: File) =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

/**
 * Último paso opcional del contratista: sube sus documentos (solo PDF) y la IA
 * los clasifica en las casillas. Con al menos uno se puede calcular en cuáles
 * califica; sin ninguno, "Lo hago después" le dice dónde subirlos más tarde.
 */
export function DocumentsStep({
  sale,
  starting,
  onCalculate,
  onLater,
  onSkip,
  onBack,
}: {
  sale: SaleCopy;
  starting: boolean;
  onCalculate: () => void;
  /** "Lo hago después": la pantalla que le cuenta dónde subirlos. */
  onLater: () => void;
  /** Quien no puede subirlos (no es administrador) sigue directo a los avisos. */
  onSkip: () => void;
  onBack: () => void;
}) {
  const { data: documents } = useOnboardingDocuments();
  const upload = useUploadOnboardingDocument();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [dragging, setDragging] = useState(false);

  const setRow = (name: string, patch: Partial<UploadRow>) =>
    setRows((current) => current.map((row) => (row.name === name ? { ...row, ...patch } : row)));

  async function uploadFiles(files: File[]) {
    setRows((current) => [
      ...current,
      ...files.map((file) => ({ name: file.name, status: "uploading" as const })),
    ]);
    // Uno por uno: cada uno es un JSON de hasta 40 MB y el error es por archivo.
    for (const file of files) {
      if (!isPdf(file)) {
        setRow(file.name, { status: "error", message: "Solo se aceptan PDF." });
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        setRow(file.name, { status: "error", message: "Pesa más de 40 MB." });
        continue;
      }
      try {
        await upload.mutateAsync({ fileName: file.name, base64Data: await fileToBase64(file) });
        setRow(file.name, { status: "uploaded" });
      } catch (error: any) {
        setRow(file.name, { status: "error", message: error?.message || "No se pudo subir." });
      }
    }
  }

  const reading = documents?.unreadDocuments.filter((doc) => doc.state === "PROCESSING").length ?? 0;
  const hasDocuments =
    rows.some((row) => row.status === "uploaded") ||
    (documents?.readyDocuments ?? 0) > 0 ||
    reading > 0;
  const uploading = rows.some((row) => row.status === "uploading");

  if (documents && !documents.canManage) {
    return (
      <div className="mx-auto w-full max-w-[720px]">
        <Eyebrow tone="blue">Último paso</Eyebrow>
        <ScreenTitle>Los documentos los sube el administrador.</ScreenTitle>
        <ScreenLead>
          Solo el administrador de tu organización puede subir sus documentos. Cuando lo
          haga, verán en qué {sale.many} califican.
        </ScreenLead>
        <ActionBar onBack={onBack}>
          <ActionButton tone="blue" onClick={onSkip} arrow>
            Continuar
          </ActionButton>
        </ActionBar>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[720px]">
      <Eyebrow tone="blue">Último paso · opcional</Eyebrow>
      <ScreenTitle>Sube tus documentos y te decimos en cuáles calificas.</ScreenTitle>
      <ScreenLead>
        Los subes una vez. La IA los cruza con las bases de las primeras {sale.many} de tu rubro.
        Solo PDF.
      </ScreenLead>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          uploadFiles([...event.dataTransfer.files]);
        }}
        className={cn(
          "mt-6 flex w-full cursor-pointer items-center gap-4 rounded-[20px] border-2 border-dashed bg-white p-5 text-left transition-colors",
          dragging ? "border-blue-500 bg-blue-50" : "border-blue-200 hover:border-blue-300",
        )}
      >
        <span className="flex size-12 shrink-0 items-center justify-center rounded-[14px] bg-blue-50 text-blue-700">
          <Upload className="size-5" strokeWidth={2.4} />
        </span>
        <span>
          <span className="block text-[16px] font-bold text-slate-900">
            Suelta tus PDF aquí o haz clic para elegirlos
          </span>
          <span className="block text-[13px] text-slate-500">
            Puedes subir varios a la vez. La IA reconoce qué es cada uno.
          </span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        hidden
        onChange={(event) => {
          uploadFiles([...(event.target.files ?? [])]);
          event.target.value = "";
        }}
      />

      {rows.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {rows.map((row) => (
            <li key={row.name} className="flex items-center gap-2 text-[13px]">
              {row.status === "uploading" ? (
                <Loader2 className="size-3.5 animate-spin text-blue-600" />
              ) : row.status === "uploaded" ? (
                <Check className="size-3.5 text-emerald-600" strokeWidth={3} />
              ) : (
                <FileText className="size-3.5 text-red-500" />
              )}
              <span className="truncate text-slate-700">{row.name}</span>
              {row.message && <span className="text-red-600">· {row.message}</span>}
            </li>
          ))}
        </ul>
      )}

      {documents && (
        <div className="mt-5 rounded-[18px] border border-slate-200 bg-white p-4">
          <p className="text-[13px] font-semibold text-slate-500">
            {reading > 0
              ? `Leyendo ${reading} ${reading === 1 ? "documento" : "documentos"}…`
              : "Lo que la IA ya reconoció"}
          </p>
          <ul className="mt-2.5 space-y-2">
            {documents.slots.map((slot) => (
              <li key={slot.type} className="flex items-start gap-3">
                <span
                  className={cn(
                    "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full",
                    slot.loaded ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-300",
                  )}
                >
                  <Check className="size-3" strokeWidth={3} />
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-[14px] font-semibold text-slate-900">
                    {slot.title}
                    {slot.essential && (
                      <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10.5px] font-bold uppercase text-blue-700">
                        Esencial
                      </span>
                    )}
                  </span>
                  <span className="block text-[12.5px] text-slate-500">
                    {slot.loaded && slot.detail ? slot.detail : slot.hint}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 flex items-center gap-1.5 text-[12.5px] text-slate-500">
        <Lock className="size-3.5" />
        Tus documentos son privados. Solo se usan para calcular tu calificación.
      </p>

      <ActionBar onBack={onBack}>
        {/* Un solo camino a la vez: sin documentos se sigue; con alguno, se calcula. */}
        {hasDocuments || uploading ? (
          <ActionButton tone="blue" onClick={onCalculate} loading={starting || uploading}>
            Calcular en cuáles califico
          </ActionButton>
        ) : (
          <ActionButton tone="blue" onClick={onLater}>
            Lo hago después
          </ActionButton>
        )}
      </ActionBar>
    </div>
  );
}
